import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { SelectField, TextField } from "@/components/Field";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { call, CALLABLES } from "@/services/callables";
import { formatNumber } from "@/lib/format";
import { COUNTRIES, zoneForCountry, zoneIsAvailable } from "./zone";
import {
  licenceProblems,
  parseSeats,
  STUDENT_SEATS_MAX,
  TIER_MAX_TEACHERS,
  TIERS,
} from "./licence";
import type { Country, Institution, LicenceTier, Locale, Segment, Zone } from "@/types";

/**
 * Creating an institution.
 *
 * The zone is **shown and not chosen**: it derives from the country, and the
 * field explains that rather than being absent, because an admin who cannot see
 * where a customer's data will live has to go and ask somebody.
 *
 * Nothing comes back to write down. The owner's way in is a link the backend
 * mails to their address, and the only thing this screen has to say about it
 * is whether that mail went — and, if it did not, where the button is that
 * sends it again.
 */

interface Created {
  institution: Institution;
  owner: { uid: string; displayName: string; username: string; email: string };
  signInLinkSent: boolean;
}

export function NewInstitutionPage() {
  const t = useT();
  const { locale: uiLocale } = useI18n();
  const navigate = useNavigate();
  const { success, error, showError } = useToast();

  const [name, setName] = useState("");
  const [country, setCountry] = useState<Country>("DE");
  const [segment, setSegment] = useState<Segment>("school");
  const [locale, setLocale] = useState<Locale>("de");
  const [tier, setTier] = useState<LicenceTier>("small");
  const [seats, setSeats] = useState(3);
  const [students, setStudents] = useState("");
  const [validUntil, setValidUntil] = useState(
    new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10)
  );
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const zone: Zone = zoneForCountry(country);
  const studentSeats = parseSeats(students);
  const problems = licenceProblems({ tier, teacherSeats: seats, studentSeats });
  const licenceFits = Object.keys(problems).length === 0;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await call<Record<string, unknown>, Created>(CALLABLES.createInstitution, {
        name: name.trim(),
        country,
        segment,
        locale,
        licenceTier: tier,
        teacherSeats: seats,
        studentSeats,
        validUntil: new Date(`${validUntil}T00:00:00.000Z`).toISOString(),
        ownerDisplayName: ownerName.trim(),
        ownerEmail: ownerEmail.trim(),
        billingEmail: billingEmail.trim(),
      });

      success("institution.new.created", { name: result.institution.name });
      /*
        The institution exists either way. A mail that did not go is an error
        the admin has to act on — it stays on screen until dismissed, and
        names the button that fixes it.
      */
      if (result.signInLinkSent) {
        success("institution.new.linkSent", { email: result.owner.email });
      } else {
        error("institution.new.linkNotSent", {
          name: result.institution.name,
          email: result.owner.email,
        });
      }
      navigate(`/institutions/${result.institution.id}`);
    } catch (caught) {
      showError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionShell titleKey="institution.new.title">
      <form className="flex max-w-xl flex-col gap-5" onSubmit={onSubmit}>
        <TextField
          labelKey="institution.new.name"
          required
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        <SelectField
          labelKey="institution.new.country"
          value={country}
          onChange={(event) => setCountry(event.target.value as Country)}
          options={COUNTRIES.map((code) => ({ value: code, labelKey: code }))}
        />

        <div>
          <p className="field-label">{t("institution.new.zone")}</p>
          <div className="flex gap-2">
            {(["eu", "us"] as const).map((candidate) => (
              <span
                key={candidate}
                aria-current={candidate === zone ? "true" : undefined}
                aria-disabled={!zoneIsAvailable(candidate)}
                className={`rounded-md border px-3 py-2 text-sm ${
                  candidate === zone
                    ? "border-cue-green bg-cue-green-soft text-ink"
                    : zoneIsAvailable(candidate)
                      ? "border-paper-deep bg-white text-ink-soft"
                      : "border-paper-deep bg-paper-warm text-ink-faint"
                }`}
              >
                {t(`zone.${candidate}`)}
              </span>
            ))}
          </div>
          <p className="mt-2 text-sm text-ink-muted">{t("institution.new.zoneDerived")}</p>
          {/*
            The US zone is real in the design and not in the world. An American
            institution currently lives in the EU deployment, which is a
            decision worth telling them about rather than letting them find out.
          */}
          <p className="mt-1 text-sm text-cue-amber">{t("institution.new.zoneUsUnavailable")}</p>
        </div>

        <SelectField
          labelKey="institution.new.segment"
          value={segment}
          onChange={(event) => setSegment(event.target.value as Segment)}
          options={[
            { value: "school", labelKey: "institutions.segment.school" },
            { value: "studio", labelKey: "institutions.segment.studio" },
          ]}
        />

        <SelectField
          labelKey="institution.new.locale"
          value={locale}
          onChange={(event) => setLocale(event.target.value as Locale)}
          options={[
            { value: "de", labelKey: "common.locale.de" },
            { value: "en", labelKey: "common.locale.en" },
          ]}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            labelKey="institution.new.tier"
            value={tier}
            onChange={(event) => setTier(event.target.value as LicenceTier)}
            options={TIERS.map((value) => ({ value, labelKey: `tier.${value}` }))}
          />
          <TextField
            labelKey="institution.new.validUntil"
            type="date"
            required
            value={validUntil}
            onChange={(event) => setValidUntil(event.target.value)}
          />
          <TextField
            labelKey="institution.new.seats"
            type="number"
            min={1}
            max={TIER_MAX_TEACHERS[tier]}
            value={String(seats)}
            errorKey={problems.teacherSeats}
            errorParams={{ tier }}
            onChange={(event) => setSeats(Number(event.target.value))}
          />
          <TextField
            labelKey="institution.new.students"
            hintKey="institution.new.studentsHint"
            type="number"
            required
            min={1}
            max={STUDENT_SEATS_MAX}
            value={students}
            // Required is enough while the field is empty; the message is for
            // a number that is there and wrong.
            errorKey={students.trim() === "" ? undefined : problems.studentSeats}
            errorParams={{ max: formatNumber(uiLocale, STUDENT_SEATS_MAX) }}
            onChange={(event) => setStudents(event.target.value)}
          />
        </div>

        <TextField
          labelKey="institution.new.ownerName"
          required
          value={ownerName}
          onChange={(event) => setOwnerName(event.target.value)}
        />
        <TextField
          labelKey="institution.new.ownerEmail"
          type="email"
          required
          value={ownerEmail}
          onChange={(event) => setOwnerEmail(event.target.value)}
        />
        <TextField
          labelKey="institution.new.billingEmail"
          type="email"
          required
          value={billingEmail}
          onChange={(event) => setBillingEmail(event.target.value)}
        />

        <div>
          <Button
            type="submit"
            busy={busy}
            disabled={
              !licenceFits ||
              name.trim().length === 0 ||
              ownerName.trim().length === 0 ||
              ownerEmail.trim().length === 0
            }
          >
            {t("institution.new.create")}
          </Button>
        </div>
      </form>
    </SectionShell>
  );
}
